// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  User, 
  Building2, 
  Globe, 
  Users,
  Star,
  Calendar
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface AffiliateApplication {
  id: string;
  prospect_id: string;
  affiliate_profile_id?: string;
  application_data: any;
  marketing_channels?: string[];
  experience_level?: string;
  expected_monthly_referrals?: number;
  commission_tier: string;
  status: string;
  reviewed_by?: string;
  review_notes?: string;
  approved_at?: string;
  rejected_at?: string;
  created_at: string;
  updated_at: string;
  affiliate_prospects?: {
    name: string;
    email: string;
    company_name?: string;
  };
}

interface AffiliateApplicationReviewProps {
  applications: AffiliateApplication[];
  onApplicationUpdate: (application: AffiliateApplication) => void;
  onReview: (application: AffiliateApplication) => void;
}

const AffiliateApplicationReview: React.FC<AffiliateApplicationReviewProps> = ({
  applications,
  onApplicationUpdate,
  onReview
}) => {
  const [selectedApplication, setSelectedApplication] = useState<AffiliateApplication | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [commissionTier, setCommissionTier] = useState('bronze');
  const [loading, setLoading] = useState(false);

  const commissionTiers = [
    { value: 'bronze', label: 'Bronze (10%)', color: 'bg-orange-100 text-orange-800' },
    { value: 'silver', label: 'Silver (15%)', color: 'bg-gray-100 text-gray-800' },
    { value: 'gold', label: 'Gold (20%)', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'platinum', label: 'Platinum (25%)', color: 'bg-purple-100 text-purple-800' }
  ];

  const experienceLevels = {
    beginner: { label: 'Beginner', color: 'bg-green-100 text-green-800' },
    intermediate: { label: 'Intermediate', color: 'bg-blue-100 text-blue-800' },
    expert: { label: 'Expert', color: 'bg-purple-100 text-purple-800' }
  };

  const handleReviewApplication = (application: AffiliateApplication) => {
    setSelectedApplication(application);
    setCommissionTier(application.commission_tier);
    setReviewNotes(application.review_notes || '');
    setReviewModalOpen(true);
  };

  const handleApproveApplication = async () => {
    if (!selectedApplication) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('affiliate_applications')
        .update({
          status: 'approved',
          commission_tier: commissionTier,
          review_notes: reviewNotes,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedApplication.id)
        .select()
        .single();

      if (error) throw error;

      onApplicationUpdate(data);
      toast.success('Application approved successfully');
      setReviewModalOpen(false);
      setSelectedApplication(null);
    } catch (error) {
      console.error('Error approving application:', error);
      toast.error('Failed to approve application');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectApplication = async () => {
    if (!selectedApplication) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('affiliate_applications')
        .update({
          status: 'rejected',
          review_notes: reviewNotes,
          rejected_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedApplication.id)
        .select()
        .single();

      if (error) throw error;

      onApplicationUpdate(data);
      toast.success('Application rejected');
      setReviewModalOpen(false);
      setSelectedApplication(null);
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error('Failed to reject application');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCommissionTierColor = (tier: string) => {
    const tierConfig = commissionTiers.find(t => t.value === tier);
    return tierConfig?.color || 'bg-gray-100 text-gray-800';
  };

  if (applications.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Pending Applications</h3>
          <p className="text-muted-foreground">
            All applications have been reviewed. New applications will appear here for review.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Applications Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {applications.map((application) => {
          const prospect = application.affiliate_prospects;
          
          return (
            <Card key={application.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">
                      {prospect?.name || 'Unknown Applicant'}
                    </CardTitle>
                    {prospect?.company_name && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Building2 className="h-3 w-3" />
                        {prospect.company_name}
                      </div>
                    )}
                  </div>
                  <Badge className={getStatusColor(application.status)}>
                    {application.status.replace('_', ' ')}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Application Details */}
                <div className="space-y-2">
                  {application.experience_level && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Experience:</span>
                      <Badge className={experienceLevels[application.experience_level as keyof typeof experienceLevels]?.color}>
                        {experienceLevels[application.experience_level as keyof typeof experienceLevels]?.label}
                      </Badge>
                    </div>
                  )}
                  
                  {application.expected_monthly_referrals && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Expected Referrals:</span>
                      <span className="text-sm font-medium">
                        {application.expected_monthly_referrals}/month
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Commission Tier:</span>
                    <Badge className={getCommissionTierColor(application.commission_tier)}>
                      {commissionTiers.find(t => t.value === application.commission_tier)?.label}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Applied:</span>
                    <span className="text-sm">
                      {format(new Date(application.created_at), 'MMM dd, yyyy')}
                    </span>
                  </div>
                </div>

                {/* Marketing Channels */}
                {application.marketing_channels && application.marketing_channels.length > 0 && (
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">Marketing Channels:</div>
                    <div className="flex flex-wrap gap-1">
                      {application.marketing_channels.slice(0, 3).map((channel, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {channel}
                        </Badge>
                      ))}
                      {application.marketing_channels.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{application.marketing_channels.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Application Data Preview */}
                {application.application_data && (
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">Application Summary:</div>
                    <div className="text-sm p-2 bg-muted rounded text-muted-foreground">
                      {typeof application.application_data === 'object' 
                        ? JSON.stringify(application.application_data).substring(0, 100) + '...'
                        : application.application_data.toString().substring(0, 100) + '...'
                      }
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleReviewApplication(application)}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Affiliate Application</DialogTitle>
          </DialogHeader>

          {selectedApplication && (
            <div className="space-y-6">
              {/* Applicant Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Applicant Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">{selectedApplication.affiliate_prospects?.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <p className="font-medium">{selectedApplication.affiliate_prospects?.email}</p>
                  </div>
                  {selectedApplication.affiliate_prospects?.company_name && (
                    <div>
                      <span className="text-muted-foreground">Company:</span>
                      <p className="font-medium">{selectedApplication.affiliate_prospects.company_name}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Applied:</span>
                    <p className="font-medium">
                      {format(new Date(selectedApplication.created_at), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Application Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Application Details</h3>
                <div className="space-y-3">
                  {selectedApplication.experience_level && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Experience Level:</span>
                      <Badge className={experienceLevels[selectedApplication.experience_level as keyof typeof experienceLevels]?.color}>
                        {experienceLevels[selectedApplication.experience_level as keyof typeof experienceLevels]?.label}
                      </Badge>
                    </div>
                  )}
                  
                  {selectedApplication.expected_monthly_referrals && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expected Monthly Referrals:</span>
                      <span className="font-medium">{selectedApplication.expected_monthly_referrals}</span>
                    </div>
                  )}
                  
                  {selectedApplication.marketing_channels && (
                    <div>
                      <span className="text-muted-foreground">Marketing Channels:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedApplication.marketing_channels.map((channel, index) => (
                          <Badge key={index} variant="outline">
                            {channel}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Commission Tier Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Commission Assignment</h3>
                <div>
                  <Label htmlFor="commission_tier">Commission Tier</Label>
                  <Select value={commissionTier} onValueChange={setCommissionTier}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {commissionTiers.map(tier => (
                        <SelectItem key={tier.value} value={tier.value}>
                          {tier.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Review Notes */}
              <div>
                <Label htmlFor="review_notes">Review Notes</Label>
                <Textarea
                  id="review_notes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add your review notes here..."
                  rows={4}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setReviewModalOpen(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleRejectApplication}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </Button>
                <Button 
                  onClick={handleApproveApplication}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  {loading ? 'Processing...' : 'Approve'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliateApplicationReview;